import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff } from '../../services/teamService';
import { listTeamRoster } from '../../services/rosterService';
import { hasProgramManagementAuthorityForTeam, isProgramOwnerForTeam, beginTeamPreseason, deleteSetupTeam, getProgram } from '../../services/programService';
import { roleLabel, sortRoles, toCoachingStaffOnlyByRoles } from '../../utils/teamRoles';
import TeamHubLayout from '../../components/teams/TeamHubLayout';

function TeamDetailInner() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [staff, setStaff] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Program-level authority for THIS team — a completely separate check
  // from myRoles/isTeamAdmin below (team_staff-based). Setup-lifecycle
  // actions (Begin Preseason, Delete Setup Team) are Program Owner/Admin
  // authority, never Team Admin — fetched regardless of the team's
  // current season_status so the code stays simple; harmless when the
  // team is past Setup, since the controls below are never rendered then.
  const [hasProgramAuthority, setHasProgramAuthority] = useState(false);
  const [isProgramOwner, setIsProgramOwner] = useState(false);
  const [beginPreseasonOpen, setBeginPreseasonOpen] = useState(false);
  const [beginningPreseason, setBeginningPreseason] = useState(false);
  const [beginPreseasonError, setBeginPreseasonError] = useState(null);
  const [deleteSetupOpen, setDeleteSetupOpen] = useState(false);
  const [deletingSetup, setDeletingSetup] = useState(false);
  const [deleteSetupError, setDeleteSetupError] = useState(null);
  // Captured ONCE from the navigation state CreateSeasonTeamPage passed
  // in (lazy initializer — only runs on this component's first render),
  // never re-read from location.state directly for display — same
  // one-time-flash pattern as MyProgramsPage's relinquish banner, and
  // for the identical reason: react-router-dom persists `state` on the
  // browser history entry, which survives a hard refresh and a later
  // back/forward navigation to it. An ordinary render-time read of
  // location.state, with nothing that ever clears it, would show this
  // banner on every future visit to this team's page, not just once.
  const [justCreatedInProgram] = useState(() => Boolean(location.state?.justCreatedInProgram));
  // Phase C.3.1 — same "View Public Page" pattern C.2b established for
  // My Programs/Program Dashboard, now added here too (browser testing
  // found no way to reach a published Team's public page from its own
  // private Overview). Fetched separately since getTeam() doesn't carry
  // the owning Program's slug — best-effort; a failure here just means
  // the link doesn't render, never blocks the rest of this page.
  const [programSlug, setProgramSlug] = useState(null);

  useEffect(() => {
    Promise.all([
      getTeam(teamId), listTeamStaff(teamId), listTeamRoster(teamId),
      hasProgramManagementAuthorityForTeam(teamId), isProgramOwnerForTeam(teamId),
    ])
      .then(([t, s, r, hasAuthority, isOwner]) => {
        setTeam(t); setStaff(s); setRoster(r);
        setHasProgramAuthority(hasAuthority);
        setIsProgramOwner(isOwner);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (!team?.program_id) { setProgramSlug(null); return; }
    let cancelled = false;
    getProgram(team.program_id).then(p => { if (!cancelled) setProgramSlug(p?.slug || null); }).catch(() => { if (!cancelled) setProgramSlug(null); });
    return () => { cancelled = true; };
  }, [team?.program_id]);

  const handleBeginPreseason = async () => {
    setBeginPreseasonError(null);
    setBeginningPreseason(true);
    try {
      const updated = await beginTeamPreseason(teamId);
      setTeam(updated);
      setBeginPreseasonOpen(false);
    } catch (e) {
      setBeginPreseasonError(e.message || 'Could not begin Preseason.');
    } finally {
      setBeginningPreseason(false);
    }
  };

  const handleDeleteSetupTeam = async () => {
    setDeleteSetupError(null);
    setDeletingSetup(true);
    try {
      await deleteSetupTeam(teamId);
      navigate(team?.program_id ? `/my-programs/${team.program_id}` : '/my-teams', {
        state: { deletedSetupTeam: team?.team_name || null },
      });
    } catch (e) {
      setDeleteSetupError(e.message || 'Could not delete this Setup Team.');
      setDeletingSetup(false);
    }
    // No `finally` — on success this component navigates away.
  };

  useEffect(() => {
    // Consumes the one-time navigation state — see MyProgramsPage's
    // identical fix for the full reasoning. A `replace` navigation to
    // the exact same URL with an empty state overwrites this history
    // entry in place, so a hard refresh or later back/forward can never
    // resurrect it. Self-terminating: the replace call itself changes
    // location.state to {}, re-running this effect once more with the
    // guard false, so it does nothing the second time — no infinite
    // loop, no eslint-disable needed.
    if (location.state?.justCreatedInProgram) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const myRoles = useMemo(
    () => staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role),
    [staff, currentUser]
  );
  const isTeamAdmin = myRoles.includes('team_admin');

  // One entry per person, with all of their active roles on this team
  // combined — team_staff has one row per role, not per person.
  const staffByPerson = useMemo(() => {
    const byUser = {};
    staff.filter(s => s.status === 'active').forEach(s => {
      if (!byUser[s.user_id]) byUser[s.user_id] = { userId: s.user_id, name: s.display_name || s.email, roles: [] };
      byUser[s.user_id].roles.push(s.role);
    });
    return Object.values(byUser);
  }, [staff]);

  const activeRoster = useMemo(() => roster.filter(r => r.status === 'active'), [roster]);

  // Head Coach highlight for the Staff card below — the one Team-People
  // fact not already covered by the Roster/Staff preview cards
  // themselves (roster count, full staff list). Not a new backend call —
  // derived from the same `staff` this page already loads. Looks at the
  // full staffByPerson (never the coaching-only filtered view below) —
  // Head Coach is unaffected by the Team-Admin display filter either way.
  const headCoach = useMemo(
    () => staffByPerson.find(p => p.roles.includes('head_coach')) || null,
    [staffByPerson]
  );

  // A viewer holding no team_staff role at all and no Program authority
  // for this team is, in current Team Hub terms, a read-only connected
  // guardian (033) — same two authority signals this page already
  // computes for its own "Manage Team Settings" button, reused here
  // rather than inferring guardian status a new way. Team Admin remains
  // a real, fully unchanged team_staff role for everyone — this only
  // controls what a non-staff viewer is SHOWN in the Staff card below.
  const isReadOnlyViewer = myRoles.length === 0 && !hasProgramAuthority;
  const displayedStaffByPerson = isReadOnlyViewer ? toCoachingStaffOnlyByRoles(staffByPerson) : staffByPerson;

  // Guardian Overview only — Head Coach already gets its own dedicated
  // line above; showing that same person again in a generic list right
  // underneath duplicated them. Excludes whoever is already shown as
  // Head Coach by userId (not just by role), so a person holding BOTH
  // Head Coach and Assistant Coach is never rendered a second time here
  // even though they also technically hold 'assistant_coach' — Head
  // Coach is the primary presentation for that person, per product
  // decision. Admin/staff viewers never use this — their card keeps the
  // existing full, unfiltered list unchanged.
  const assistantCoaches = useMemo(
    () => displayedStaffByPerson.filter(p => p.roles.includes('assistant_coach') && p.userId !== headCoach?.userId),
    [displayedStaffByPerson, headCoach]
  );

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  return (
    <TeamHubLayout team={team} active="overview">
      {justCreatedInProgram && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Season Team created. Assign a Head Coach to get started, or do it later from Staff.
          {isTeamAdmin && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained" color="primary" size="small"
                onClick={() => navigate(`/my-teams/${teamId}/staff`, { state: { preselectRole: 'head_coach' } })}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Assign Head Coach →
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {(isTeamAdmin || hasProgramAuthority) && (
        // Discoverability only (030 correction) — a Program Owner/Admin
        // already has real Team Settings authority as of 028/029
        // (structured classification/football type/format, the Program-
        // authority Archive/Reactivate path) even when they aren't also
        // represented as legacy team_admin, so this button must reflect
        // "has at least one legitimate reason to be there," not just the
        // legacy team_admin RLS path. hasProgramAuthority is the SAME
        // value already loaded above for the Setup-lifecycle controls —
        // no new fetch. This does not mean everything on Team Settings
        // becomes editable — that page enforces its own per-section
        // authority (team_admin-gated fields vs. Program-authority-gated
        // classification/format/archive) exactly as before; this is only
        // whether the button/link is shown.
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          {/* Phase C.3.1 — secondary/subtle action, same visual weight
              relationship as Manage Program vs. View Public Page (C.2b) —
              never rendered without a resolved slug, and never rendered
              at all while the Team isn't published, so there is no
              misleading link for an unpublished Team here. */}
          {team.public_page_enabled && programSlug && (
            <Button
              size="small"
              onClick={() => navigate(`/programs/${programSlug}/teams/${teamId}`)}
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.78rem', color: '#6a8aa8' }}
            >
              View Public Page ↗
            </Button>
          )}
          <Button variant="outlined" color="primary" size="small" onClick={() => navigate(`/my-teams/${teamId}/settings`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Manage Team Settings
          </Button>
        </Box>
      )}

      {/* Setup lifecycle — Program management authority only (Owner or
          Program Admin), never Team Admin/Head Coach in this phase. Only
          rendered while the team is actually in Setup; gone entirely once
          it moves to Preseason. */}
      {team.season_status === 'setup' && hasProgramAuthority && (
        <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>This Season Team is in Setup</Typography>
            <Typography sx={{ color: '#9eb8d0', fontSize: '0.9rem', mb: 2 }}>
              Use this time to prepare and verify the Team before it becomes part of the Program's official season.
            </Typography>
            <Button
              variant="contained" color="primary"
              onClick={() => setBeginPreseasonOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Begin Preseason
            </Button>
          </CardContent>
        </Card>
      )}

      {team.season_status === 'setup' && isProgramOwner && (
        <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid #4a1010' }}>
          <Typography sx={{ fontWeight: 700, color: '#f44336', mb: 1 }}>Delete Setup Team</Typography>
          <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mb: 2 }}>
            Only for a Team created by mistake during Setup. This cannot be undone, and is no longer available once this Team enters Preseason.
          </Typography>
          {deleteSetupError && <Alert severity="error" sx={{ mb: 2 }}>{deleteSetupError}</Alert>}
          <Button
            variant="outlined" color="error"
            onClick={() => setDeleteSetupOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Delete Setup Team
          </Button>
        </Box>
      )}

      <Dialog
        open={beginPreseasonOpen}
        onClose={() => { if (!beginningPreseason) setBeginPreseasonOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Begin Preseason?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            This Season Team will move out of Setup and into Preseason.
          </Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 2 }}>
            Before continuing, confirm that the Team's basic season configuration is correct.
          </Typography>

          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Program</Typography>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{team.organization_name || '—'}</Typography>

          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Team</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{team.team_name}</Typography>

          <Typography sx={{ color: '#9eb8d0' }}>
            Once Preseason begins, this Team becomes part of the Program's official season history and can no longer be permanently deleted as a Setup mistake.
          </Typography>

          {beginPreseasonError && <Alert severity="error" sx={{ mt: 2 }}>{beginPreseasonError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setBeginPreseasonOpen(false)} disabled={beginningPreseason} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleBeginPreseason}
            disabled={beginningPreseason}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {beginningPreseason ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Begin Preseason'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSetupOpen}
        onClose={() => { if (!deletingSetup) setDeleteSetupOpen(false); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Setup Team?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>This permanently deletes:</Typography>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>{team.team_name}</Typography>

          <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
            This action is intended only for Teams created by mistake during Setup. Once a Team enters Preseason, it becomes part of Program history and can no longer be permanently deleted through this action.
          </Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 1.5 }}>
            Any coaches or staff assigned during Setup, and any join codes generated for this Team, will also be removed.
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            This cannot be undone.
          </Typography>

          {deleteSetupError && <Alert severity="error" sx={{ mt: 2 }}>{deleteSetupError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteSetupOpen(false)} disabled={deletingSetup} sx={{ textTransform: 'none', color: '#9eb8d0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSetupTeam}
            disabled={deletingSetup}
            variant="contained" color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {deletingSetup ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete Team'}
          </Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 800 }}>Roster</Typography>
            <Button size="small" onClick={() => navigate(`/my-teams/${teamId}/roster`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
              View full roster →
            </Button>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {activeRoster.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>No athletes on the roster yet.</Typography>
          ) : (
            <Typography sx={{ color: '#9eb8d0' }}>
              {activeRoster.length} active athlete{activeRoster.length === 1 ? '' : 's'}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 800 }}>{isReadOnlyViewer ? 'Coaching Staff' : 'Staff'}</Typography>
            <Button size="small" onClick={() => navigate(`/my-teams/${teamId}/staff`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
              View full staff →
            </Button>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Head Coach</Typography>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{headCoach ? headCoach.name : 'Not yet assigned'}</Typography>
          {isReadOnlyViewer ? (
            <>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>Assistant Coaches</Typography>
              {assistantCoaches.length === 0 ? (
                <Typography sx={{ fontWeight: 700 }}>None assigned</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {assistantCoaches.map(person => (
                    <Typography key={person.userId} sx={{ fontWeight: 700 }}>{person.name}</Typography>
                  ))}
                </Box>
              )}
            </>
          ) : displayedStaffByPerson.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>No active staff.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {displayedStaffByPerson.map(person => (
                <Box key={person.userId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{person.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {sortRoles(person.roles).map(r => (
                      <Chip key={r} size="small" label={roleLabel(r)} sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </TeamHubLayout>
  );
}

export default function TeamDetailPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamDetailInner /></RequireAuth>
    </Box>
  );
}
